// import { jwtDecode } from "jwt-decode";
import {
  axiosArisChatInstance,
  axiosChatInstance,
} from "../AxiosConfiguration";
import Cookies from "js-cookie";
import { v4 as uuidv4 } from "uuid";

let isFetching = false;

// Function to parse streaming response and handle events
export const parseStreamingResponse = async (response, onMetadata, onChunk, onError) => {
  if (!response || !response.body) {
    const error = new Error('Invalid response: Response or response.body is undefined');
    console.error(error.message);
    onError && onError(error);
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let accumulatedMessage = ''; // Store the full message as chunks arrive

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        console.log('Stream complete');
        break;
      }

      buffer += decoder.decode(value, { stream: true });
      const events = buffer.split('\n\n'); // SSE events are separated by double newlines
      buffer = events.pop() || ''; // Keep incomplete event in buffer

      for (const event of events) {
        if (event.trim()) {
          try {
            // Extract 'data:' field from SSE event
            const dataLine = event.split('\n').find((l) => l.startsWith('data:'));
            if (!dataLine) continue;

            const jsonString = dataLine.replace(/^data:\s*/, '').trim();
            if (jsonString === '[DONE]') {
              break;
            }

            const parsedEvent = JSON.parse(jsonString);

            if (parsedEvent.type === 'metadata') {
              // Pass metadata to callback (e.g., store conversation_id and session_id)
              onMetadata && onMetadata(parsedEvent);
            } else if (parsedEvent.type === 'chunk') {
              const content = parsedEvent.content || '';
              accumulatedMessage += content; // Append to the full message
              onChunk && onChunk({
                type: 'chunk',
                content: content,
                accumulated: accumulatedMessage,
                conversation_id: parsedEvent.conversation_id,
                session_id: parsedEvent.session_id,
              });
            } else if (parsedEvent.type === 'response') {
              // The final response contains the complete message
              accumulatedMessage = parsedEvent.data || accumulatedMessage;
              onChunk && onChunk({
                type: 'response',
                content: accumulatedMessage,
                conversation_id: parsedEvent.conversation_id,
                session_id: parsedEvent.session_id,
                isFinal: true,
              });
            } else if (parsedEvent.type === 'error') {
              console.warn('Received error event:', parsedEvent);
              onError && onError(new Error(parsedEvent.error || 'Unknown error'));
            }
          } catch (parseError) {
            console.warn('Failed to parse streaming event:', event, parseError);
            onError && onError(parseError);
          }
        }
      }
    }
  } catch (error) {
    console.error('Error reading stream:', error);
    onError && onError(error);
  } finally {
    reader.releaseLock();
  }
};

// Voice coaching history (grouped like chat recent)
export const getVoiceCoachingHistory = async (limit = 30, skip = 0, grouped = true, voice_session = true) => {
  try {
    const params = new URLSearchParams();
    params.set("limit", String(limit));
    params.set("skip", String(skip));
    if (grouped) {
      params.set("grouped", "true");
      try {
        const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (userTimeZone) params.set("timezone", userTimeZone);
      } catch (_) {
        // best-effort: if Intl API fails, omit timezone
      }
    }

    const response = await axiosArisChatInstance.get(
      `/ARIS/v2/chat/voice-coaching-history?${params.toString()}`
    );
    return response.data;
  } catch (error) {
    console.error("Error during API call:", error);
    return error;
  }
};

// Get relationship chat history
export const getRelationshipChatHistory = async (limit = 30, skip = 0, grouped = false) => {
  try {
    const params = new URLSearchParams();
    params.set("limit", String(limit));
    params.set("skip", String(skip));
    params.set("module", "RELATIONSHIPS");
    if (grouped) params.set("grouped", "true");

    const response = await axiosArisChatInstance.get(
      `ARIS/v2/chat/recent?${params.toString()}`
    );
    return response.data;
  } catch (error) {
    console.error("Error during API call:", error);
    return error;
  }
};

// Get unified session history (chat + voice)
export const getSessionHistory = async (limit = 30, includeChat = true, includeVoice = true, grouped = true) => {
  try {
    const params = new URLSearchParams();
    params.set("limit", String(limit));
    params.set("include_chat", String(includeChat));
    params.set("include_voice", String(includeVoice));
    params.set("grouped", String(grouped));

    const response = await axiosArisChatInstance.get(
      `/ARIS/v2/chat/session-history?${params.toString()}`
    );
    return response.data;
  } catch (error) {
    console.error("Error during API call:", error);
    return error;
  }
};

// Search conversations (typeahead)
// Optional module filter: e.g., 'COACHING', 'DIALOGS', 'RELATIONSHIPS'
export const searchChatSessions = async (q, limit = 10, skip = 0, module) => {
  try {
    const params = new URLSearchParams();
    params.set("q", q ?? "");
    params.set("limit", String(limit));
    params.set("skip", String(skip));
    
    // Handle multiple modules (array) or single module (string)
    if (Array.isArray(module)) {
      module.forEach(mod => params.append("module", mod));
    } else if (module) {
      params.set("module", module);
    }

    const response = await axiosArisChatInstance.get(
      `/ARIS/v2/chat/search?${params.toString()}`
    );
    return response.data; // { success, data: [...], count }
  } catch (error) {
    console.error("Error during API call:", error);
    return error;
  }
};

// Rename conversation title
export const renameConversationTitle = async (session_id, title) => {
  try {
    const params = new URLSearchParams();
    params.set("session_id", session_id);
    params.set("title", title);

    const response = await axiosArisChatInstance.patch(
      `/ARIS/v2/chat/conversation/title?${params.toString()}`
    );
    return response.data; // { success, ... }
  } catch (error) {
    console.error("Error during API call:", error);
    return error;
  }
};

// Delete conversation
export const deleteConversation = async (session_id) => {
  try {
    const params = new URLSearchParams();
    params.set("session_id", session_id);

    const response = await axiosArisChatInstance.delete(
      `/ARIS/v2/chat/conversation?${params.toString()}`
    );
    return response.data; // { success, ... }
  } catch (error) {
    console.error("Error during API call:", error);
    return error;
  }
};

export const chatArisApi = async (
  user_id,
  query,
  session_id = null,
  module = "COACHING",
  stream = true,
  additional_params = {},
  event = "send_message",
  metadata = undefined,
  topicConfig = null // { topic_id, quick: true } for quick coaching topics
) => {
  try {
    const belief_system = JSON.parse(localStorage.getItem("beliefSystem"));

    // Build payload
    const payload = {
      query: query,
      user_id: user_id,
      ...additional_params,
      ...(metadata ? { metadata } : {}),
    };

    // Add topic configuration for quick coaching topics (only for new sessions, not history)
    if (topicConfig) {
      // Only add topic_id and option_id if they exist (new topic session)
      if (topicConfig.topic_id) {
        payload.topic_id = topicConfig.topic_id;
      }
      if (topicConfig.option_id) {
        payload.option_id = topicConfig.option_id;
      }
      // Only add quick flag for new topic sessions with topic_id
      if (topicConfig.quick && topicConfig.topic_id) {
        payload.quick = true;
      }
    }

    // Include belief_system if faith is enabled by user
    if (belief_system?.toggle_state && belief_system?.faith) {
      payload.belief_system = { faith: belief_system.faith };
    }
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Determine module - use "TOPIC_COACHING" if topicConfig is set (either new session or from history)
    const effectiveModule = topicConfig ? "TOPIC_COACHING" : module;

    // Initialize params
    const params = {
      event: event,
      stream: stream,
      module: effectiveModule,
      data: {
        payload,
      },
      timezone: userTimeZone,
    };

    // Add session_id if provided to continue existing session
    if (session_id) {
      params.data.payload.session_id = session_id;
    }
    let accept_content_type = 'application/json';
    if (stream) {
      accept_content_type = 'text/event-stream';
    }
    const response = await fetch(`${process.env.REACT_APP_BASE_URL_CHAT_ARIS}/ARIS/v2/chat/message`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': accept_content_type,
        Authorization: `Bearer ${Cookies.get("idtoken")}`,
      },
      body: JSON.stringify(params),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    if (!response.body) {
      throw new Error('Response body is undefined');
    }

    // If stream, return the response body for streaming use (e.g., ReadableStream)
    if (stream) {
      return response;
    }

    // Else parse JSON and return it
    const json = await response.json();
    return json;
  } catch (error) {
    console.error('Error during API call:', error);
    throw error; // Rethrow to handle in caller
  }
};

export const chatArisApiRelationShip = async (userId, query, sessionId, additional_params, stream = true, event) => {
  try {
    return await chatArisApi(userId, query, sessionId, "RELATIONSHIPS", stream, additional_params, event)
  } catch (error) {
    console.error("Error during API call:", error);
    return error;
  }
};

/**
 * Start or continue an Elements Assessment chat session.
 *
 * @param {string} userId - MongoDB user ID
 * @param {string} query - User message text
 * @param {string|null} sessionId - Existing session_id (null for new)
 * @param {string|null} conversationId - Existing conversation_id (null for new)
 * @param {string|null} topic - Domain: "Resolution" | "Cognition" | "Judgment" | "Motivation"
 * @param {boolean} stream - Whether to stream (SSE)
 * @returns {Promise<Response>} Raw fetch Response (for streaming) or parsed JSON
 */
export const chatElementsAssessmentApi = async (
  userId,
  query,
  sessionId = null,
  conversationId = null,
  topic = null,
  stream = true,
) => {
  try {
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const payload = {
      user_id: userId,
      query,
      session_id: sessionId,
      conversation_id: conversationId,
      timezone: userTimeZone,
      time: new Date().toISOString(),
    };

    if (topic) {
      payload.topic = topic;
    }

    const params = {
      event: "send_message",
      module: "ELEMENTS_ASSESSMENT",
      data: { payload },
      stream,
      timezone: userTimeZone,
    };

    const acceptType = stream ? "text/event-stream" : "application/json";

    const response = await fetch(
      `${process.env.REACT_APP_BASE_URL_CHAT_ARIS}/ARIS/v2/chat/message`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: acceptType,
          Authorization: `Bearer ${Cookies.get("idtoken")}`,
        },
        body: JSON.stringify(params),
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    if (!response.body) {
      throw new Error("Response body is undefined");
    }

    if (stream) {
      return response;
    }

    return await response.json();
  } catch (error) {
    console.error("Error during Elements Assessment API call:", error);
    throw error;
  }
};

export const chatArisApiInitiate = async (userId) => {
  try {
    const response = await axiosArisChatInstance.post(
      "/ARIS/coaching/instruction/chat",
      {
        user_id: userId,
        callback_url:
          process.env.REACT_APP_BASE_URL_ASSESSMENT +
          "/api/status/fetch/user/portrait",
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error during API call:", error);
    return error;
  }
};

export const initiateChatSession = async () => {
  return {
    "payload": {
      "chat_id": uuidv4(),
    }
  }
};

export const createMsg = async (chat_session_id, msg_body) => {
  try {
    //init Params
    const params = {
      user_id: Cookies.get("localId"),
      chat_session_id: chat_session_id, // chat_session_id,
      msg_body: msg_body,
    };

    //api call to send chat data
    const response = await axiosChatInstance.post(
      "api/chat/create/message",
      params
    );
    //return API response
    return response.data;
  } catch (error) {
    console.error("Error during API call:", error);
    return error;
  }
};

export const getChatSessionList = async (limit = 30, skip = 0, grouped = true, module = "COACHING") => {
  try {
    // api call to fetch recent conversations (supports grouped view)
    const params = new URLSearchParams();
    params.set("limit", String(limit));
    params.set("skip", String(skip));
    
    // Handle multiple modules (array) or single module (string)
    if (Array.isArray(module)) {
      module.forEach(mod => params.append("module", mod));
    } else if (module !== 'All') {
      params.set("module", module);
    }

    if (grouped) {
      params.set("grouped", "true");
      try {
        const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (userTimeZone) params.set("timezone", userTimeZone);
      } catch (_) {
        // best-effort: if Intl API fails, omit timezone
      }
    }

    const response = await axiosArisChatInstance.get(
      `ARIS/v2/chat/recent?${params.toString()}`
    );
    // return API response (shape: { success, data, count })
    return response.data;
  } catch (error) {
    console.error("Error during API call:", error);
    return error;
  }
};

export const getChatHistory = async (
  session_id,
) => {
  try {
    //api call to send chat data
    const response = await axiosArisChatInstance.get(
      `ARIS/v2/chat/history?session_id=${session_id}`
    );
    return response.data;
  } catch (error) {
    console.error("Error during API call:", error);
    return error;
  }
};

export const getLatestChatHistory = async () => {
  try {

    //api call to send chat data
    const response = await axiosArisChatInstance.get(
      `ARIS/v2/chat/latest-conversation`,
    );

    return response.data;
  } catch (error) {
    console.error("Error during API call:", error);
    return error;
  }
};


export const getChatinitialVoice = async () => {
  try {
    const response = await axiosChatInstance.get(`/api/static/initial_voice`);
    return response.data;
  } catch (error) {
    console.error("Error during API call:", error);
    throw error;
  }
};

export const onChatEnd = async (session_id, user_id, conversation_id, module = "COACHING") => {
  try {
    const payload = {
      session_id: session_id,
      user_id: user_id,
      module: module,
      conversation_id: conversation_id
    };

    const response = await axiosArisChatInstance.post(
      "/ARIS/v2/chat/end-session",
      payload
    );

    return response.data;
  } catch (error) {
    console.error("Error during API call:", error);
  }
};

export const dynamicDashboard = async (user_id) => {
  try {
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const response = await axiosArisChatInstance.get(
      `/ARIS/box/my_boxes?user_id=${user_id}&timezone=${userTimeZone}`
    );

    return response?.data;
  } catch (error) {
    console.error("Error during API call:", error);
    throw error;
  }
};
